import { ApiClientBase } from '@@/api/api';
import { ApiErrorResult, ApiOKResult } from '@@/types/ApiResult';

export const BannerSeverityMap = {
  Low: 'low',
  Moderate: 'moderate',
  High: 'high',
  Critical: 'critical'
} as const;

export type BannerSeverity = (typeof BannerSeverityMap)[keyof typeof BannerSeverityMap];

export interface Banner {
  bannerId: string;
  message: string;
  severity: BannerSeverity;
  pageLocations: string[];
  isActive: boolean;
  hideLogin: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BannerResponse {
  ok: boolean;
  data: Banner & {
    message?: string;
    error?: string;
  };
}

export interface BannerErrorResponse {
  ok: boolean;
  error: string;
  message: string;
}

export interface CreateBannerRequest {
  message: string;
  severity: BannerSeverity;
  pageLocations: string[];
  hideLogin?: boolean;
}

export interface UpdateBannerRequest {
  message?: string;
  severity?: BannerSeverity;
  pageLocations?: string[];
  isActive?: boolean;
  hideLogin?: boolean;
}

export interface PageLocationsPayload {
  pageLocations: string[];
  message?: string;
}

export class OutageNotificationsClient extends ApiClientBase {
  async getActiveBanner(page: string, baseUrlOverride?: string): Promise<ApiOKResult<Banner> | ApiErrorResult> {
    try {
      const url = this.getOutageNotificationsApiUrl(`/active-banner/${encodeURIComponent(page)}`, baseUrlOverride);
      const response = await fetch(url, {
        method: 'GET'
      });

      if (!response.ok) {
        if (response.status === 404) {
          return this.error('No active banner found');
        }
        const errorBody = await response.json().catch(() => ({}));
        return this.error(errorBody?.error || errorBody?.message || 'Failed to fetch active banner');
      }

      const data: { ok: boolean; data: Banner } = await response.json();
      return {
        ok: true,
        data: data.data,
        message: data.data.message || 'Active banner retrieved successfully'
      };
    } catch (error) {
      console.error('Error fetching active banner:', error);
      return this.error(error instanceof Error ? error.message : 'Failed to fetch active banner');
    }
  }
  async getPageLocations(): Promise<ApiOKResult<PageLocationsPayload> | ApiErrorResult> {
    try {
      const url = this.getOutageNotificationsApiUrl('/page-locations');
      const response = await fetch(url, {
        method: 'GET',
        headers: await this.getHeaders()
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        return this.error(errorBody?.error || errorBody?.message || 'Failed to fetch page locations');
      }

      const data: { ok: boolean; data: PageLocationsPayload } = await response.json();
      return {
        ok: true,
        data: data.data,
        message: data.data.message || 'Page locations retrieved successfully'
      };
    } catch (error) {
      console.error('Error fetching page locations:', error);
      return this.error(error instanceof Error ? error.message : 'Failed to fetch page locations');
    }
  }
  async getBanner(): Promise<ApiOKResult<Banner> | ApiErrorResult> {
    try {
      const url = this.getOutageNotificationsApiUrl('/banner');
      const response = await fetch(url, {
        method: 'GET',
        headers: await this.getHeaders()
      });

      if (!response.ok) {
        if (response.status === 404) {
          return this.error('No banner found');
        }
        const errorBody = await response.json().catch(() => ({}));
        return this.error(errorBody?.error || errorBody?.message || 'Failed to fetch banner');
      }

      const data: BannerResponse = await response.json();
      return {
        ok: true,
        data: data.data,
        message: data.data.message || 'Banner retrieved successfully'
      };
    } catch (error) {
      console.error('Error fetching banner:', error);
      return this.error(error instanceof Error ? error.message : 'Failed to fetch banner');
    }
  }

  async createBanner(request: CreateBannerRequest): Promise<ApiOKResult<Banner> | ApiErrorResult> {
    try {
      const url = this.getOutageNotificationsApiUrl('/banners');
      const response = await fetch(url, {
        method: 'POST',
        headers: await this.getHeaders(),
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        return this.error(errorBody?.error || errorBody?.message || 'Failed to create banner');
      }

      const data: BannerResponse = await response.json();
      return {
        ok: true,
        data: data.data,
        message: data.data.message || 'Banner created successfully'
      };
    } catch (error) {
      console.error('Error creating banner:', error);
      return this.error(error instanceof Error ? error.message : 'Failed to create banner');
    }
  }

  async updateBanner(bannerId: string, request: UpdateBannerRequest): Promise<ApiOKResult<Banner> | ApiErrorResult> {
    try {
      const url = this.getOutageNotificationsApiUrl(`/banners/${bannerId}`);
      const response = await fetch(url, {
        method: 'PUT',
        headers: await this.getHeaders(),
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        return this.error(errorBody?.error || errorBody?.message || 'Failed to update banner');
      }

      const data: BannerResponse = await response.json();
      return {
        ok: true,
        data: data.data,
        message: data.data.message || 'Banner updated successfully'
      };
    } catch (error) {
      console.error('Error updating banner:', error);
      return this.error(error instanceof Error ? error.message : 'Failed to update banner');
    }
  }

  async fetchActiveBannerFromRegions(fallbackBase: string, page: string) {
    const primaryPromise = this.getActiveBanner(page);
    const fallbackPromise = this.getActiveBanner(page, fallbackBase);

    const first = await Promise.any([primaryPromise, fallbackPromise]);

    if (first.ok) {
      return { banner: first.data, hideLogin: first.data.hideLogin };
    }

    return { banner: null, hideLogin: false };
  }
}
